<template>
	<DexDataTable
		:value="masterDataColumns"
		:loading="loading"
		class="p-datatable-sm editable-cells-table"
		:autoLayout="true"
		columnResizeMode="fit"
		editMode="cell"
		@cell-edit-init="onCellEdit"
		@cell-edit-complete="onCellCompleteEditing"
		@row-dblclick="openEditDialog($event.data)"
		:paginator="true"
		:rows="tableListPagination.pageSize"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:scrollable="true"
		scrollHeight="flex"
		@page="onChangePage"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.MasterData.Columns') }) }}
		</template>

		<Column field="name" :header="$t('Squeeze.MasterData.Name')" class="column-cell" style="min-width: 10rem;">
			<template #body="masterDataColumn">
				<div class="column-cell" v-tooltip.bottom="masterDataColumn.data.name">
					{{ masterDataColumn.data.name }}
				</div>
			</template>
			<template #editor="masterDataColumn" v-if="!checkPermissions">
				<InputText v-model="masterDataColumn.data.name" @dblclick="openEditDialog(masterDataColumn.data)" @keyup="changeEventInInput(masterDataColumn.data)"/>
			</template>
		</Column>
		<Column field="description" :header="$t('Squeeze.MasterData.Description')" class="column-cell" style="min-width: 10rem;">
			<template #body="masterDataColumn">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(masterDataColumn.data)">
					{{ getTranslatedDescription(masterDataColumn.data) }}
				</div>
			</template>
			<template #editor="masterDataColumn" v-if="!checkPermissions">
				<InputText v-model="masterDataColumn.data.description" @dblclick="openEditDialog(masterDataColumn.data)" @keyup="changeEventInInput(masterDataColumn.data)"/>
			</template>
		</Column>
		<Column field="sourceColumn" :header="$t('Squeeze.MasterData.SourceColumn')" class="column-cell" style="min-width: 10rem;">
			<template #body="masterDataColumn">
				<div class="column-cell" v-tooltip.bottom="masterDataColumn.data.sourceColumn">
					{{ masterDataColumn.data.sourceColumn }}
				</div>
			</template>
			<template #editor="masterDataColumn" v-if="!checkPermissions">
				<InputText v-model="masterDataColumn.data.sourceColumn" @dblclick="openEditDialog(masterDataColumn.data)" @keyup="changeEventInInput(masterDataColumn.data)"/>
			</template>
		</Column>
		<Column field="active" filterField="active" :header="$t('Squeeze.MasterData.Translatable')" headerClass="header-center">
			<template #body="masterDataColumn">
				<Checkbox @change="onChangeCheckbox(masterDataColumn.data)" v-model="masterDataColumn.data.translatable" :binary="true" />
			</template>
		</Column>
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="masterDataColumn">
				<small v-if="isInvalidMasterDataColumn(masterDataColumn.data)" class="p-error column-cell">
					<div v-if="!masterDataColumn.data.name" v-tooltip.bottom="$t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.Name')})">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.Name')}) }}
					</div>
					<div v-if="!masterDataColumn.data.description" v-tooltip.bottom="$t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.Description')})">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.Description')}) }}
					</div>
					<div v-if="!masterDataColumn.data.sourceColumn" v-tooltip.bottom="$t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.SourceColumn')})">
						{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.MasterData.SourceColumn')}) }}
					</div>
				</small>
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="masterDataColumn">
				<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEditDialog(masterDataColumn.data)" :disabled="checkPermissions" />
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(masterDataColumn.data)" :disabled="checkPermissions" />
			</template>
		</Column>

		<template #paginatorstart>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button p-mr-2" @click="onNewEntryClick" :disabled="checkPermissions" />
			<Button :label="$t('Squeeze.MasterData.CreateResetTable')" type="button" icon="mdi mdi-refresh" class="p-button refreshButton" @click="resetTable" :disabled="checkPermissions" />
		</template>
		<template #paginatorend></template>
	</DexDataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {DataTableCellEditCompleteEvent} from "primevue/datatable";
import DexDataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, ref} from "vue";
import {MasterDataColumn} from "@dex/squeeze-client-ts";
import {helpers} from "@vuelidate/validators";
import Tooltip from "primevue/tooltip";
import useSqueezeStore from "@/apps/squeeze/store";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import {ClientManager} from "@/singletons/ClientManager";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import Checkbox from "primevue/checkbox";

export default defineComponent({
	name: "MasterDataColumns",
	components: {
		DexDataTable, Column, InputText, Checkbox,
	},
	props: {
		masterDataColumns: {
			type: Array as PropType<MasterDataColumn[]>,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		permissions: {
			type: Object,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "onResetClick", "onChangeInputField", "onChangeCheckbox",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** The event.data values to check it */
		const entryDataCheck = reactive<MasterDataColumn>({id: 0,tableId: 0, description: '', sourceColumn: '', name: ''});

		/** Is InputField changed?  */
		const changeInputField = ref<boolean>(false);

		/** Check masterData-Permissions */
		const checkPermissions = computed(() => {
			if (props.permissions) {
				return props.permissions.permissionEdit && !props.permissions.permissionManage
					|| store.state.featureSet.masterDataTablePermissions && !props.permissions.permissionEdit && !props.permissions.permissionManage
					|| !store.state.featureSet.masterDataTablePermissions && !store.state.scopes.sqzAdmin;
			} else {
				return false
			}
		});

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: MasterDataColumn) => {
			emit("onClickDelete", row);
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", {id: undefined, name: '', description: '', sourceColumn: ''})
		}

		/** Triggered when the New Entry Button is clicked */
		const openEditDialog = (row: MasterDataColumn) => {
			// check if permissions return true, to prevent double click
			if (checkPermissions.value) {
				return
			}
			emit("onEntrySelect", row)
		}

		/** Triggered when the Reset-Button is clicked */
		const resetTable = () => {
			emit("onResetClick");
		}

		/** Check the MasterDataColumn is invalid */
		const isInvalidMasterDataColumn = (row: MasterDataColumn) => {
			return (!helpers.req(row.name) || !helpers.req(row.description) || !helpers.req(row.sourceColumn));
		}

		/**
		 * Focus the editing cell
		 * @param event
		 */
		const onCellEdit = async (event: DataTableCellEditCompleteEvent|any) => {
			await nextTick();
			Object.assign(entryDataCheck, event.data);
			event.originalEvent.target.childNodes[2].focus();

			// This is needed when a text is clicked directly
			if (event.originalEvent.currentTarget && event.originalEvent.currentTarget.childNodes[2]) {
				event.originalEvent.currentTarget.childNodes[2]?.focus();
			}
		}

		/**
		 * Check if value is changed, then emit & Triggered on change of any field
		 * @param event
		 */
		const onCellCompleteEditing = (event: DataTableCellEditCompleteEvent) => {
			if (changeInputField.value) {
				emit("onChangeInputField", event, isInvalidMasterDataColumn(event.newData));
				changeInputField.value = false;
			} else {
				Object.assign(entryDataCheck, null);

				// We need to trigger this, so the handler move the focus to the next cell is executed
				document.dispatchEvent(new CustomEvent('reloadDone', { detail: event }));
			}
		}

		/**
		 * Check if input was changed
		 * @param inputData
		 */
		const changeEventInInput = (inputData: unknown) => {
			if (JSON.stringify(inputData) !== JSON.stringify(entryDataCheck)) {
				changeInputField.value = true;
			}
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		/**
		 * Triggered when a checkbox is clicked
		 * @param {MasterDataColumn} data Data to edit
		 */
		const onChangeCheckbox = (data: MasterDataColumn) => {
			emit("onChangeCheckbox", data)
		}

		return {
			getTranslatedDescription,
			t,
			toast,
			store,
			tableListPagination,
			entryDataCheck,
			changeInputField,
			checkPermissions,
			openDeleteDialog,
			onNewEntryClick,
			openEditDialog,
			resetTable,
			isInvalidMasterDataColumn,
			onCellEdit,
			onCellCompleteEditing,
			changeEventInInput,
			onChangePage,
			onChangeCheckbox,
		}
	},
});
</script>

<style scoped>

input {
	width: 100%;
	border: none;
	border-radius: 0;
}

::v-deep(td.column-cell.p-editable-column.p-cell-editing) {
	padding: 0;
}

::v-deep(td.column-cell) {
	width: 20%;
}

.refreshButton {
	background-color: var(--error-color);
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

</style>
